// tslint:disable: no-inferrable-types
// tslint:disable: member-ordering
import * as Utils from './utils';
import { State } from './state.model';
import { BackendObjectRef } from './backend-integration.service';
import { TransitionGuard } from './transition-guard.model';
import { TransitionAction } from './transition-action.model';

export interface TransitionConfirmationInfo {
    requireConfirmation: boolean;
    title?: string;
    description?: string;
    continueButtonTitle?: string;
    cancelButtonTitle?: string;
}

const DEFAULT_CONFIRMATION_INFO: TransitionConfirmationInfo = {
    requireConfirmation: false
};

export class Transition implements BackendObjectRef {
    private static ID_PREFIX = 'wt-';

    public static transitionFromPartial(value: Partial<Transition>): Transition {
        if (!value.roles || !value.name || !value.source || !value.target) {
            throw new Error($localize `Required properties (roles, name, source, target) not provided!`);
        }

        const transition =
            new Transition(value.id,
                           value.roles,
                           value.name,
                           value.source,
                           value.target,
                           value.actions,
                           value.autoTransition,
                           value.timeTriggeredTransition,
                           value.timeExpiration,
                           value.confirmation,
                           value.guards,
                           value.message,
                           value.requireValidForm);

        return transition;
    }

    /**
     * Initial transition factory
     *
     * Initial transition is a special transition with pre-defined name and
     * source state. Currently the EB Backend accepts only `actions`, `roles`
     * and `target state` therefore all other attributes will be lost
     * during serialization/deserialization.
     *
     * @param target State Workflow will be after the Transition is executed
     * @param roles Roles which can execute the Transition
     * @param actions Actions to run when Transition is executed
     * @param name Name of the transition
     */
    public static initialTransition(target: State,
                                    roles: BackendObjectRef[],
                                    actions?: TransitionAction[],
                                    name: string = 'Apply'): Transition {
        const transition = new Transition(undefined,
                                          roles,
                                          name,
                                          State.initialState,
                                          target,
                                          actions,
                                          true);
        return transition;
    }

    public readonly id: string;

    private _draft: boolean = false;
    public get isDraft(): boolean { return this._draft; }
    public set isDraft(draft: boolean) { this._draft = draft; }

    public get isInitialTransition(): boolean {
        return this.source.isInitialState;
    }

    public roles: BackendObjectRef[];
    public name: string;
    public source: State;
    public target: State;

    public actions: TransitionAction[];
    public autoTransition: boolean;
    public timeTriggeredTransition: boolean;
    public timeExpiration: number;
    public confirmation: TransitionConfirmationInfo;
    public guards: TransitionGuard[];
    public message: string;
    public requireValidForm: boolean;

    public constructor(id: string|undefined,
                       roles: BackendObjectRef[],
                       name: string,
                       source: State,
                       target: State,
                       actions?: TransitionAction[],
                       autoTransition?: boolean,
                       timeTriggeredTransition?: boolean,
                       timeExpiration?: number,
                       confirmation?: TransitionConfirmationInfo,
                       guards?: TransitionGuard[],
                       message?: string,
                       requireValidForm?: boolean) {
        this.id = id ?? Utils.objectIdentifier(Transition.ID_PREFIX);
        this.isDraft = !id ? true : false;
        this.roles = roles;
        this.name = name;
        this.source = source;
        this.target = target;

        this.actions = actions ?? [];
        this.autoTransition = autoTransition ?? false;
        this.timeTriggeredTransition = timeTriggeredTransition ?? false;
        this.timeExpiration = timeExpiration ?? 0;
        this.confirmation = confirmation ?? DEFAULT_CONFIRMATION_INFO;
        this.guards = guards ?? [];
        this.message = message ?? '';
        this.requireValidForm = requireValidForm ?? true;
    }

    public copy() {
        const transition =
            new Transition(this.id,
                           this.roles,
                           this.name,
                           this.source,
                           this.target,
                           this.actions,
                           this.autoTransition,
                           this.timeTriggeredTransition,
                           this.timeExpiration,
                           this.confirmation,
                           this.guards,
                           this.message,
                           this.requireValidForm);

        return transition;
    }
}
